import React from "react";
import { Box, Button, Card, Chip, Divider, Stack, Typography } from "@mui/joy";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import type {
  LlmRouteAttempt,
  LlmRouteProvenance,
} from "./llmRouteProvenanceTypes";

interface LlmRouteProvenanceChipProps {
  provenance: LlmRouteProvenance | null | undefined;
  inline?: boolean;
}

/**
 * Renders the backend-authored LLM route provenance for an assistant message:
 * a collapsed chip showing the selected direct model, expanding to the ordered
 * contributing-operation / fallback / capacity attempt sequence.
 *
 * IMPORTANT: this component only DISPLAYS fields from the provenance payload. It
 * never computes, derives, or infers any provenance value. Chip colors are
 * presentation-only styling of the verbatim status/decision strings.
 */

// Presentation-only: map a verbatim status/decision string to a chip color.
// This styles the label; it does not compute any provenance value.
const toneForStatus = (
  value: string | null | undefined,
): "success" | "warning" | "danger" | "neutral" => {
  const s = (value || "").toLowerCase();
  if (s.includes("fail") || s.includes("error") || s.includes("exhaust")) {
    return "danger";
  }
  if (s.includes("skip") || s.includes("reroute") || s.includes("fallback")) {
    return "warning";
  }
  if (s.includes("ok") || s.includes("success") || s.includes("dispatch")) {
    return "success";
  }
  return "neutral";
};

const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 1,
    }}
  >
    <Typography level="body-xs" color="neutral">
      {label}
    </Typography>
    <Typography level="body-xs" sx={{ textAlign: "right", wordBreak: "break-word" }}>
      {value}
    </Typography>
  </Box>
);

const AttemptRow: React.FC<{ attempt: LlmRouteAttempt; index: number }> = ({
  attempt,
  index,
}) => (
  <Stack spacing={0.5} data-testid={`llm-provenance-attempt-${index}`}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        level="body-sm"
        data-testid={`llm-provenance-attempt-${index}-model`}
      >
        {attempt.provider}/{attempt.model}
      </Typography>
      <Chip
        size="sm"
        variant="soft"
        color={toneForStatus(attempt.status)}
        data-testid={`llm-provenance-attempt-${index}-status`}
      >
        {attempt.status}
      </Chip>
    </Box>
    {attempt.entry_reason && (
      <FieldRow label="Route reason" value={attempt.entry_reason} />
    )}
    {attempt.error_reason && (
      <FieldRow label="Fallback reason" value={attempt.error_reason} />
    )}
    {attempt.error_class && (
      <FieldRow label="Error class" value={attempt.error_class} />
    )}
    <FieldRow label="Capacity" value={attempt.capacity.decision} />
    <FieldRow
      label="Duration"
      value={attempt.duration_ms !== null ? `${attempt.duration_ms} ms` : "n/a"}
    />
  </Stack>
);

export const LlmRouteProvenanceChip: React.FC<LlmRouteProvenanceChipProps> = ({
  provenance,
  inline = false,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const panelId = React.useId();

  if (!provenance) {
    return null;
  }

  const selected = provenance.selected_profile;
  const observed = provenance.observed;
  // Presentation-only label selection (field display, not computation).
  const chipLabel = selected
    ? selected.model
    : observed?.observed_model || provenance.operation_key;

  return (
    <Stack
      spacing={0.75}
      sx={{
        width: inline ? "auto" : "100%",
        maxWidth: "100%",
        minWidth: 0,
        alignItems: inline ? "flex-start" : "stretch",
      }}
      data-testid="llm-provenance-card"
    >
      <Button
        size="sm"
        variant="soft"
        color="neutral"
        onClick={() => setExpanded((prev) => !prev)}
        startDecorator={<AltRouteIcon fontSize="small" />}
        sx={{
          borderRadius: 6,
          px: 1.25,
          minHeight: 30,
          alignSelf: inline ? "flex-start" : "flex-end",
        }}
        data-testid="llm-provenance-toggle"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
      >
        <Box
          component="span"
          data-testid="llm-provenance-selected-model"
          sx={{ whiteSpace: "nowrap" }}
        >
          {chipLabel}
        </Box>
      </Button>
      {expanded && (
        <Card
          variant="soft"
          id={panelId}
          data-testid="llm-provenance-panel"
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            p: 1.5,
            borderRadius: 6,
            border: "1px solid",
            borderColor: "divider",
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography level="title-sm">Model route</Typography>
              <Chip size="sm" variant="soft" color="neutral">
                {provenance.operation_key}
              </Chip>
            </Box>

            {selected && (
              <FieldRow
                label="Selected model"
                value={
                  <Box component="span" data-testid="llm-provenance-selected-detail">
                    {selected.provider}/{selected.model}
                    {selected.model_version &&
                    selected.model_version !== selected.model
                      ? ` (${selected.model_version})`
                      : ""}
                  </Box>
                }
              />
            )}
            {selected?.deployment && (
              <FieldRow label="Deployment" value={selected.deployment} />
            )}
            {observed && (
              <FieldRow
                label="Observed model"
                value={
                  <Box component="span" data-testid="llm-provenance-observed-model">
                    {observed.observed_model}
                  </Box>
                }
              />
            )}
            {observed?.finish_reason && (
              <FieldRow label="Finish reason" value={observed.finish_reason} />
            )}
            <FieldRow label="Route revision" value={provenance.route_revision} />
            {provenance.runtime_transition_count > 0 && (
              <FieldRow
                label="Runtime transitions"
                value={provenance.runtime_transition_count}
              />
            )}
            {provenance.terminal_failure_kind && (
              <Box>
                <Chip
                  size="sm"
                  variant="soft"
                  color="danger"
                  data-testid="llm-provenance-terminal-failure"
                >
                  {provenance.terminal_failure_kind}
                </Chip>
              </Box>
            )}

            {provenance.attempts.length > 0 && (
              <>
                <Divider />
                <Typography level="body-xs" color="neutral">
                  Attempts ({provenance.attempts.length})
                </Typography>
                <Stack spacing={1}>
                  {provenance.attempts.map((attempt, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider />}
                      <AttemptRow attempt={attempt} index={index} />
                    </React.Fragment>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

export default LlmRouteProvenanceChip;
